import React, { useState } from 'react';
import { frameworks } from '../mock';
import { Code2, Layers, GitCompareArrows, History, ArrowRight } from 'lucide-react';

export default function ESGAsCode() {
  const [active, setActive] = useState(0);
  const f = frameworks[active];

  return (
    <section id="code" className="relative">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">
              ESG-as-Code®
            </div>
            <h2 className="mt-3 font-display text-[40px] lg:text-[54px] leading-[1.02] text-[#0B1F2D]">
              Embedded
              <br />
              <span className="italic">compliance logic.</span>
            </h2>
            <p className="mt-6 text-[16px] leading-[1.6] text-[#0B1F2D]/70 max-w-[480px]">
              ESG-as-Code® converts regulatory obligations into deterministic, executable rules. Every compliance outcome
              maps to a specific clause in a specific regulation. Auditable, traceable, and defensible.
            </p>

            <ul className="mt-8 space-y-4">
              {[
                { Ico: Code2, t: 'Maps regulatory text to structured requirements and executable rule logic.' },
                { Ico: Layers, t: 'Runs rule sets across your ESG report and supporting documents.' },
                { Ico: GitCompareArrows, t: 'Traces every finding back to a specific clause, section, and evidence source.' },
                { Ico: History, t: 'Adapts as regulations and guidance evolve, with version history retained.' },
              ].map(({ Ico, t }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="shrink-0 w-7 h-7 rounded-md border border-[#E8E5DC] bg-white flex items-center justify-center text-[#1B6F5E]">
                    <Ico className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-[14.5px] leading-[1.55] text-[#0B1F2D]/80">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: frameworks interactive */}
          <div className="lg:col-span-7">
            <div className="rounded-[22px] border border-[#E8E5DC] bg-white overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E8E5DC] flex items-center justify-between bg-[#FAFAF5]/50">
                <div className="text-[11.5px] tracking-[0.12em] uppercase text-[#0B1F2D]/60">
                  Seven live frameworks · 90+ rules
                </div>
                <div className="font-mono-tight text-[11px] text-[#0B1F2D]/50">/rulesets</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12">
                {/* Tabs */}
                <div className="md:col-span-5 border-r border-[#E8E5DC]">
                  {frameworks.map((fw, i) => (
                    <button
                      key={fw.code}
                      onClick={() => setActive(i)}
                      className={`w-full text-left px-5 py-4 flex items-center justify-between border-b border-[#E8E5DC] last:border-b-0 transition-colors ${
                        active === i ? 'bg-[#FAFAF5]' : 'bg-white hover:bg-[#FAFAF5]/60'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`font-mono-tight text-[10px] px-2 py-1 rounded ${
                            active === i
                              ? 'bg-[#0B1F2D] text-[#FAFAF5]'
                              : 'bg-[#EEEEEA] text-[#0B1F2D]/70'
                          }`}
                        >
                          {fw.code}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-medium text-[#0B1F2D] truncate">{fw.title}</div>
                          <div className="text-[11px] text-[#0B1F2D]/50">{fw.region}</div>
                        </div>
                      </div>
                      <div className="text-[11px] text-[#0B1F2D]/55 font-mono-tight shrink-0">
                        {fw.rules} rules
                      </div>
                    </button>
                  ))}
                </div>

                {/* Detail */}
                <div className="md:col-span-7 p-7 lg:p-9">
                  <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-[0.14em] text-[#1B6F5E]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1B6F5E]" /> Active framework
                  </div>
                  <div className="mt-3 font-display text-[34px] lg:text-[42px] leading-[1.05] text-[#0B1F2D]">
                    {f.title}
                  </div>
                  <div className="mt-2 text-[13px] text-[#0B1F2D]/55">Jurisdiction: {f.region}</div>
                  <p className="mt-5 text-[13.5px] leading-[1.6] text-[#0B1F2D]/75">{f.desc}</p>

                  <div className="mt-7 grid grid-cols-3 gap-3">
                    <div className="rounded-xl border border-[#E8E5DC] bg-[#FAFAF5]/50 p-4">
                      <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">Rules</div>
                      <div className="mt-1 font-display text-[28px] leading-none text-[#0B1F2D]">{f.rules}</div>
                    </div>
                    <div className="rounded-xl border border-[#E8E5DC] bg-[#FAFAF5]/50 p-4">
                      <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">Layer</div>
                      <div className="mt-1 font-display text-[18px] leading-none text-[#0B1F2D]">AI + Code</div>
                    </div>
                    <div className="rounded-xl border border-[#E8E5DC] bg-[#FAFAF5]/50 p-4">
                      <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">Output</div>
                      <div className="mt-1 font-display text-[18px] leading-none text-[#0B1F2D]">Clause-traced</div>
                    </div>
                  </div>

                  <a
                    href="#workflow"
                    className="mt-7 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#0B1F2D] link-underline"
                  >
                    See how it runs in your workflow <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Two layer cards */}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-[#E8E5DC] bg-white p-6">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#1B6F5E] font-medium">Layer 01</div>
                <div className="mt-2 font-display text-[20px] text-[#0B1F2D]">AI extraction layer</div>
                <p className="mt-2 text-[13.5px] leading-[1.6] text-[#0B1F2D]/65">
                  AI reads ESG and climate disclosure documents, extracts structured fields, and organizes narratives,
                  tables, and supporting evidence.
                </p>
              </div>
              <div className="rounded-2xl border border-[#0B1F2D] bg-[#0B1F2D] text-[#FAFAF5] p-6">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#7BC2B0] font-medium">Layer 02</div>
                <div className="mt-2 font-display text-[20px]">ESG-as-Code® decision layer</div>
                <p className="mt-2 text-[13.5px] leading-[1.6] text-[#FAFAF5]/70">
                  Applies deterministic regulatory logic to evaluate compliance. Findings are rule-based, explainable
                  and traceable to specific requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
